import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  addDays,
  addMonths,
  expandEvents,
  groupOccurrencesByDay,
  monthGridDays,
  monthName,
  startOfDay,
  toDateKey,
  upcomingBirthdays,
} from '@awwyeah/shared';
import { MonthView } from './components/MonthView';
import { useCalendarStore, useHouseholdData } from './useCalendarData';
import { useDigestCalendarOpen } from './digestCardPreference';
import { useWeekStart } from './weekStartPreference';

/**
 * The month grid, as a card another surface can mount (Dan, 2026-08-18: "add a full
 * calendar to the top of the view"). Today it's the digest that mounts it.
 *
 * This lives in the calendar module and is exported from its `index.ts` for the same
 * reason `GamesSection` lives in the games module: a feature module never reaches into
 * another module's internals, so the owning module publishes the one component it's
 * willing to have embedded elsewhere and keeps everything behind it private. The digest
 * therefore stays a pure read with no calendar knowledge of its own — it doesn't know what
 * an occurrence is, how recurrence expands, or which of two docs an event came from.
 *
 * Deliberately **not** a second calendar: there is no member filter, no view switcher, no
 * create affordance and no event detail sheet here. Tapping a day leaves for the real
 * calendar, on that day. The one job is "what does this month look like", which is the
 * question the grid answers in a glance and the agenda list underneath it cannot.
 */
export function CalendarSection(): JSX.Element {
  const navigate = useNavigate();
  const { weekStartsOn } = useWeekStart();
  const store = useCalendarStore();
  const household = useHouseholdData();
  const { open, setOpen } = useDigestCalendarOpen();

  const today = useMemo(() => startOfDay(new Date()), []);
  const [cursor, setCursor] = useState(today);

  // Expanding recurrence over the *grid's* span, not the calendar month's — the six-week
  // grid shows a tail of the previous month and a head of the next, and those cells carry
  // real dots. Collapsed, the window is never computed at all.
  const window = useMemo(() => {
    const cells = monthGridDays(cursor, weekStartsOn);
    const first = cells[0] ?? cursor;
    const last = cells[cells.length - 1] ?? cursor;
    return { from: first.getTime(), to: addDays(last, 1).getTime() };
  }, [cursor, weekStartsOn]);

  const byDay = useMemo(
    () => groupOccurrencesByDay(expandEvents(store.events, window)),
    [store.events, window],
  );

  const birthdayDays = useMemo(
    () =>
      new Set(
        upcomingBirthdays(household.birthdays, { now: today, withinDays: 400 }).map(
          (entry) => entry.date,
        ),
      ),
    [household.birthdays, today],
  );

  /** A tap hands off to the real calendar on that day — see `?d=` in `CalendarPage`. */
  const openDay = useCallback((day: Date) => navigate(`/calendar?d=${toDateKey(day)}`), [navigate]);

  const showingThisMonth =
    cursor.getMonth() === today.getMonth() && cursor.getFullYear() === today.getFullYear();

  return (
    <section className="flex flex-col gap-2" data-testid="digest-calendar">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          data-testid="digest-calendar-toggle"
          className="-mx-1 flex min-h-11 items-center gap-1.5 rounded-sm px-1 text-left transition-colors hover:bg-raised"
        >
          <ChevronDown
            className={`h-4 w-4 text-fg-3 transition-transform duration-150 ease-app ${
              open ? '' : '-rotate-90'
            }`}
            aria-hidden="true"
          />
          <h2 className="font-display text-[17px] tracking-tight text-fg">{monthName(cursor)}</h2>
          <span className="text-sm text-fg-3">{cursor.getFullYear()}</span>
        </button>

        {open && (
          <div className="ml-auto flex items-center gap-0.5">
            {!showingThisMonth && (
              <button
                type="button"
                onClick={() => setCursor(today)}
                data-testid="digest-calendar-today"
                className="h-9 rounded-full border border-line px-3 text-[13px] font-semibold text-fg-2"
              >
                Today
              </button>
            )}
            <IconButton
              label="Previous month"
              testId="digest-calendar-prev"
              onClick={() => setCursor((current) => addMonths(current, -1))}
            >
              <ChevronLeft className="h-5 w-5" />
            </IconButton>
            <IconButton
              label="Next month"
              testId="digest-calendar-next"
              onClick={() => setCursor((current) => addMonths(current, 1))}
            >
              <ChevronRight className="h-5 w-5" />
            </IconButton>
          </div>
        )}
      </div>

      {open && (
        <div className="overflow-hidden rounded-sm border border-line">
          <MonthView
            month={cursor}
            today={today}
            selectedDay={today}
            byDay={byDay}
            birthdayDays={birthdayDays}
            members={household.byId}
            onSelectDay={openDay}
          />
        </div>
      )}
    </section>
  );
}

function IconButton({
  label,
  testId,
  onClick,
  children,
}: {
  label: string;
  testId: string;
  onClick: () => void;
  children: JSX.Element;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="flex h-9 w-9 items-center justify-center rounded-full text-fg-2 transition-colors hover:bg-raised"
    >
      {children}
    </button>
  );
}
